import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'

import {DopamineSessionSummary} from 'interfaces'
import {
  DopamineAssistantService,
  getDopamineAssistantBaseUrl,
  isDopamineAssistantNotFoundError,
} from 'services/api'
import {DopamineAssistantSessionController} from './DopamineAssistant.types'
import {
  createDopamineAssistantSessionId,
  DopamineAssistantStorageScope,
} from './DopamineAssistant.utils'

export const DOPAMINE_SESSIONS_QUERY_KEY = 'getDopamineAssistantSessions'
export const DOPAMINE_HISTORY_QUERY_KEY = 'getDopamineAssistantHistory'
const DOPAMINE_SESSION_WRITE_WINDOW_MS = 12 * 60 * 60 * 1000

const isSessionOutsideWriteWindow = (createdAt: string): boolean =>
  Date.now() - Date.parse(createdAt) >= DOPAMINE_SESSION_WRITE_WINDOW_MS

interface UseDopamineAssistantSessionOptions {
  enabled: boolean
  storageScope: DopamineAssistantStorageScope | null
}

export const useDopamineAssistantSession = ({
  enabled,
  storageScope,
}: UseDopamineAssistantSessionOptions): DopamineAssistantSessionController => {
  const isApiAvailable = Boolean(getDopamineAssistantBaseUrl())
  const isSessionApiEnabled = enabled && isApiAvailable && Boolean(storageScope)
  const queryClient = useQueryClient()
  const sessionsQueryKey = useMemo(
    () => [DOPAMINE_SESSIONS_QUERY_KEY, storageScope] as const,
    [storageScope],
  )
  const [clientSessionId, setClientSessionId] = useState(
    createDopamineAssistantSessionId,
  )
  const [conversationRevision, setConversationRevision] = useState(0)
  const [shouldLoadHistory, setShouldLoadHistory] = useState(false)
  const [hasConversation, setHasConversation] = useState(false)
  const [isSessionEnded, setIsSessionEnded] = useState(false)
  const shouldRefetchSessionsAfterFetchRef = useRef(false)
  const sessionsQuery = useQuery({
    enabled: isSessionApiEnabled,
    queryFn: DopamineAssistantService.getSessions,
    queryKey: sessionsQueryKey,
  })
  const historyQuery = useQuery({
    enabled: isSessionApiEnabled && shouldLoadHistory,
    queryFn: () => DopamineAssistantService.getSessionHistory(clientSessionId),
    queryKey: [DOPAMINE_HISTORY_QUERY_KEY, storageScope, clientSessionId],
  })
  const refetchSessions = sessionsQuery.refetch

  useEffect(() => {
    if (
      !shouldRefetchSessionsAfterFetchRef.current ||
      sessionsQuery.fetchStatus !== 'idle'
    ) {
      return
    }

    shouldRefetchSessionsAfterFetchRef.current = false
    void refetchSessions()
  }, [refetchSessions, sessionsQuery.fetchStatus])

  const requestSessionsRefresh = useCallback((): void => {
    if (!isSessionApiEnabled) return

    const fetchStatus = queryClient.getQueryState(sessionsQueryKey)?.fetchStatus
    if (fetchStatus === 'idle' || fetchStatus === undefined) {
      void refetchSessions()
    } else {
      shouldRefetchSessionsAfterFetchRef.current = true
    }
  }, [isSessionApiEnabled, queryClient, refetchSessions, sessionsQueryKey])

  const startNewChat = useCallback((): void => {
    const nextSessionId = createDopamineAssistantSessionId()
    setClientSessionId(nextSessionId)
    setConversationRevision(revision => revision + 1)
    setShouldLoadHistory(false)
    setHasConversation(false)
    setIsSessionEnded(false)
  }, [])

  const selectSession = useCallback((session: DopamineSessionSummary): void => {
    setClientSessionId(session.sessionId)
    setConversationRevision(revision => revision + 1)
    setShouldLoadHistory(true)
    setHasConversation(true)
    setIsSessionEnded(isSessionOutsideWriteWindow(session.createdAt))
  }, [])

  const markSessionStarted = useCallback((): void => {
    setHasConversation(true)
    if (!hasConversation) requestSessionsRefresh()
  }, [hasConversation, requestSessionsRefresh])

  const refreshSessions = requestSessionsRefresh

  const markSessionReadOnly = useCallback((): void => {
    setIsSessionEnded(true)
    setHasConversation(true)
  }, [])

  const sessions = sessionsQuery.data ?? []
  const isHistoryLoading =
    historyQuery.isLoading && historyQuery.fetchStatus !== 'idle'
  const isSessionMissing =
    historyQuery.isError && isDopamineAssistantNotFoundError(historyQuery.error)
  const isHistoryError =
    historyQuery.isError &&
    (historyQuery.data === undefined || isSessionMissing)

  return {
    clientSessionId,
    conversationRevision,
    hasConversation,
    history: shouldLoadHistory ? historyQuery.data?.messages ?? [] : [],
    isApiAvailable,
    isHistoryError,
    isHistoryLoading,
    isReadOnly: isSessionEnded,
    isHistoryRefreshing:
      shouldLoadHistory && historyQuery.isFetching && !isHistoryLoading,
    isSessionsError: sessionsQuery.isError && sessionsQuery.data === undefined,
    isSessionsLoading:
      sessionsQuery.isLoading && sessionsQuery.fetchStatus !== 'idle',
    markSessionReadOnly,
    markSessionStarted,
    refreshSessions,
    selectSession,
    sessions,
    startNewChat,
  }
}
